import { useState, useEffect, useRef } from 'react';
import { api } from '../../lib/api';
import type { ReviewContextData } from './types';

interface ReviewContextPanelProps {
  context: ReviewContextData | null | 'loading';
}

/** Section display names — raw keys from assemblePrompt (kebab-case) and
 *  token-counter TRUNCATION_ORDER (camelCase). Both forms must be handled. */
const SECTION_LABELS: Record<string, string> = {
  // Kebab-case keys from prompt-assembler sectionTokens / sectionsIncluded
  diff: 'Diff',
  'changed-files': 'Changed files',
  'imported-signatures': 'Imported signatures',
  'test-files': 'Test files',
  'recent-prs': 'Recent PRs',
  'pr-metadata': 'PR metadata',
  metadata: 'Metadata',
  conventions: 'Conventions',
  system: 'System',
  buffer: 'Buffer',
  // camelCase aliases from token-counter ContextSection (used in truncatedSections)
  fileContents: 'Changed files',
  importedSignatures: 'Imported signatures',
  testFiles: 'Test files',
  recentPRs: 'Recent PRs',
};

/** Tailwind bar colors per section */
const SECTION_COLORS: Record<string, string> = {
  diff: 'bg-teal-600',
  'changed-files': 'bg-blue-600',
  'imported-signatures': 'bg-violet-600',
  'test-files': 'bg-green-600',
  'recent-prs': 'bg-yellow-600',
  'pr-metadata': 'bg-orange-600',
  metadata: 'bg-orange-600',
  conventions: 'bg-pink-600',
  system: 'bg-gray-600',
  buffer: 'bg-gray-700',
};

function formatTokens(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function pct(part: number, total: number): number {
  if (total === 0) return 0;
  return Math.max(1, Math.round((part / total) * 100));
}

export function ReviewContextPanel({ context }: ReviewContextPanelProps) {
  const [showPrompt, setShowPrompt] = useState(false);
  const [fullPrompt, setFullPrompt] = useState<string | null>(null);
  const [loadingPrompt, setLoadingPrompt] = useState(false);

  // Ref tracks the current session key so in-flight prompt fetches can detect
  // a session switch and discard their results without a stale closure.
  const currentSessionKeyRef = useRef<string | null>(null);

  // Reset prompt viewer state when the selected session/epoch changes so that
  // a previously loaded prompt from session A is never shown under session B's metadata.
  const sessionKey = typeof context === 'object' && context !== null
    ? `${context.sessionId}:${context.epoch}`
    : null;
  useEffect(() => {
    currentSessionKeyRef.current = sessionKey;
    setShowPrompt(false);
    setFullPrompt(null);
    setLoadingPrompt(false);
  }, [sessionKey]);

  if (context === 'loading') {
    return (
      <div className="bg-gray-900 border border-gray-700 rounded-lg p-6">
        <h4 className="text-sm font-medium text-gray-300 mb-3">Context Inspection</h4>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <div className="animate-spin w-4 h-4 border-2 border-teal-400 border-t-transparent rounded-full" />
          Loading context breakdown…
        </div>
      </div>
    );
  }

  if (context === null) {
    return (
      <div className="bg-gray-900 border border-gray-700 rounded-lg p-6">
        <h4 className="text-sm font-medium text-gray-300 mb-1">Context Inspection</h4>
        <p className="text-xs text-gray-500">
          Not available — session predates Phase 19 or context TTL has expired (48 h).
        </p>
      </div>
    );
  }

  const { totalTokens, sectionTokens, sectionsIncluded, sectionsOmitted, tokenBudget } = context;

  // Sort sections by token count descending for the bar chart
  const sortedSections = Object.entries(sectionTokens).sort(([, a], [, b]) => b - a);

  async function handleTogglePrompt() {
    if (showPrompt) {
      setShowPrompt(false);
      return;
    }
    if (fullPrompt !== null) {
      setShowPrompt(true);
      return;
    }
    if (typeof context !== 'object' || context === null) return;
    // Capture session key before await to detect stale results after session switch
    const fetchKey = `${context.sessionId}:${context.epoch}`;
    setLoadingPrompt(true);
    try {
      const data = await api.reviews.context(context.sessionId, {
        includePrompt: true,
        epoch: context.epoch,
      });
      // Discard result if the user switched sessions while the fetch was in-flight
      if (currentSessionKeyRef.current !== fetchKey) return;
      setFullPrompt(data.contextMessage ?? '(empty)');
    } catch {
      if (currentSessionKeyRef.current !== fetchKey) return;
      setFullPrompt('(failed to load prompt)');
    } finally {
      setLoadingPrompt(false);
      // Only advance the show state if the fetch is still relevant to the current session
      if (currentSessionKeyRef.current === fetchKey) {
        setShowPrompt(true);
      }
    }
  }

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 space-y-5">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-300">Context Inspection</h4>
        <span className="text-xs text-gray-500">
          Epoch {context.epoch} · stored {new Date(context.storedAt).toLocaleString()}
        </span>
      </div>

      {/* Token budget overview */}
      <div>
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-xs text-gray-400 uppercase tracking-wide">Token budget</span>
          <span className="text-xs text-gray-400">
            <span
              data-testid="total-tokens"
              className={tokenBudget.withinBudget ? 'text-green-400' : 'text-red-400'}
            >
              {formatTokens(totalTokens)}
            </span>
            {' / '}
            <span className="text-gray-500">{formatTokens(tokenBudget.budgetTarget)}</span>
          </span>
        </div>

        {/* Stacked token bar */}
        <div
          data-testid="token-bar"
          className="flex h-3 rounded overflow-hidden gap-px bg-gray-800"
          title={`${totalTokens.toLocaleString()} / ${tokenBudget.budgetTarget.toLocaleString()} tokens`}
        >
          {sortedSections.map(([section, tokens]) => (
            <div
              key={section}
              data-testid={`bar-${section}`}
              className={`${SECTION_COLORS[section] ?? 'bg-gray-600'} transition-all`}
              style={{ width: `${tokenBudget.budgetTarget > 0 ? Math.round((tokens / tokenBudget.budgetTarget) * 100) : 0}%` }}
              title={`${SECTION_LABELS[section] ?? section}: ${tokens.toLocaleString()} tokens`}
            />
          ))}
        </div>

        {/* Section breakdown table */}
        <div className="mt-3 space-y-1">
          {sortedSections.map(([section, tokens]) => (
            <div key={section} className="flex items-center gap-2 text-xs">
              <span
                className={`w-2 h-2 rounded-sm flex-shrink-0 ${SECTION_COLORS[section] ?? 'bg-gray-600'}`}
              />
              <span className="text-gray-400 w-36 truncate">
                {SECTION_LABELS[section] ?? section}
              </span>
              <div className="flex-1 h-1 bg-gray-800 rounded overflow-hidden">
                <div
                  className={`h-full ${SECTION_COLORS[section] ?? 'bg-gray-600'}`}
                  style={{ width: `${pct(tokens, totalTokens)}%` }}
                />
              </div>
              <span className="text-gray-300 w-12 text-right font-mono">
                {formatTokens(tokens)}
              </span>
            </div>
          ))}
        </div>

        {/* Truncated sections warning */}
        {tokenBudget.truncatedSections.length > 0 && (
          <div className="mt-2 flex items-center gap-1 text-xs text-yellow-400">
            <span>⚠</span>
            <span>Truncated: {tokenBudget.truncatedSections.map((s) => SECTION_LABELS[s] ?? s).join(', ')}</span>
          </div>
        )}
      </div>

      {/* Enrichment status */}
      <div>
        <span className="text-xs text-gray-400 uppercase tracking-wide">Enrichment</span>
        <div className="mt-2 flex flex-wrap gap-3 text-xs">
          <span
            data-testid="enrichment-test-files"
            className={`px-2 py-1 rounded ${context.testFileCount > 0 ? 'bg-green-900 text-green-300' : 'bg-gray-800 text-gray-500'}`}
          >
            {context.testFileCount} test file{context.testFileCount !== 1 ? 's' : ''}
          </span>
          <span
            data-testid="enrichment-signatures"
            className={`px-2 py-1 rounded ${context.importedSignatureCount > 0 ? 'bg-violet-900 text-violet-300' : 'bg-gray-800 text-gray-500'}`}
          >
            {context.importedSignatureCount} signature{context.importedSignatureCount !== 1 ? 's' : ''}
          </span>
          <span
            data-testid="enrichment-recent-prs"
            className={`px-2 py-1 rounded ${context.recentPRCount > 0 ? 'bg-blue-900 text-blue-300' : 'bg-gray-800 text-gray-500'}`}
          >
            {context.recentPRCount} recent PR{context.recentPRCount !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Sections included / omitted */}
      <div className="flex flex-wrap gap-1">
        {sectionsIncluded.map((s) => (
          <span key={s} className="px-1.5 py-0.5 bg-teal-900 text-teal-300 text-xs rounded">
            {SECTION_LABELS[s] ?? s}
          </span>
        ))}
        {sectionsOmitted.map((s) => (
          <span key={s} className="px-1.5 py-0.5 bg-gray-800 text-gray-500 text-xs rounded line-through">
            {SECTION_LABELS[s] ?? s}
          </span>
        ))}
      </div>

      {/* Prompt viewer toggle */}
      <div>
        <button
          onClick={() => void handleTogglePrompt()}
          disabled={loadingPrompt}
          className="text-xs text-teal-400 hover:text-teal-300 disabled:text-gray-600 transition-colors"
          data-testid="toggle-prompt"
        >
          {loadingPrompt ? 'Loading prompt…' : showPrompt ? '▲ Hide XML prompt' : '▼ Show XML prompt'}
        </button>

        {showPrompt && fullPrompt !== null && (
          <pre
            data-testid="prompt-viewer"
            className="mt-3 p-3 bg-gray-950 border border-gray-700 rounded text-xs text-gray-300 overflow-x-auto max-h-96 font-mono whitespace-pre-wrap break-words"
          >
            {fullPrompt}
          </pre>
        )}
      </div>
    </div>
  );
}
